import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, MapPin, Calendar, Loader2, AlertCircle, CreditCard } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const TicketTier = ({ tier, name, price, description, selected, onSelect, remaining, soldOut }) => {
  const isFoundingCircle = tier === "founding_circle";
  
  return (
    <motion.div
      className={`relative border p-8 cursor-pointer transition-all duration-300 ${
        soldOut 
          ? 'border-stone-700 bg-stone-900/50 opacity-60 cursor-not-allowed'
          : selected 
            ? 'border-gold-500 bg-gold-500/5' 
            : 'border-stone-700 hover:border-stone-500 bg-void'
      }`}
      onClick={() => !soldOut && onSelect(tier)}
      whileHover={!soldOut ? { scale: 1.02 } : {}}
      data-testid={`ticket-tier-${tier}`}
    >
      {isFoundingCircle && !soldOut && remaining > 0 && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-gold-500 text-charcoal text-[10px] tracking-[0.15em] uppercase px-4 py-1 font-manrope">
            Limited
          </span>
        </div>
      )}
      
      {soldOut && (
        <div className="absolute inset-0 flex items-center justify-center bg-void/80 z-10">
          <span className="text-stone-500 font-manrope text-sm tracking-wider uppercase">Sold Out</span>
        </div>
      )}
      
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="font-cinzel text-xl text-parchment mb-1">{name}</h3>
          <p className="font-cormorant text-3xl text-gold-500">${price.toLocaleString()}</p>
        </div>
        {selected && (
          <div className="w-8 h-8 rounded-full bg-gold-500 flex items-center justify-center">
            <Check className="w-5 h-5 text-charcoal" />
          </div>
        )}
      </div>
      
      <p className="font-manrope text-sm text-stone-400 leading-relaxed">{description}</p>
    </motion.div>
  );
};

const AccessPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [accessInfo, setAccessInfo] = useState(null);
  const [ticketStats, setTicketStats] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [pokerInterest, setPokerInterest] = useState(false);
  const [streamingInterest, setStreamingInterest] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState(null);

  // Check for payment success
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accessRes, statsRes] = await Promise.all([
          axios.get(`${API}/access/${token}`),
          axios.get(`${API}/stats/tickets`)
        ]);
        
        setAccessInfo(accessRes.data);
        setTicketStats(statsRes.data);
        
        if (accessRes.data.already_purchased) {
          setPurchaseDetails(accessRes.data.purchase_details);
          setSuccess(true);
        }
      } catch (err) {
        console.error("Failed to fetch access info:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Check payment status if session_id present
  useEffect(() => {
    if (sessionId && !success) {
      const checkPayment = async () => {
        try {
          const res = await axios.get(`${API}/payments/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            setSuccess(true);
            // Refetch access info
            const accessRes = await axios.get(`${API}/access/${token}`);
            if (accessRes.data.already_purchased) {
              setPurchaseDetails(accessRes.data.purchase_details);
            }
          }
        } catch (err) {
          console.error("Payment check error:", err);
        }
      };
      checkPayment();
    }
  }, [sessionId, token, success]);

  const handleCheckout = async () => {
    if (!selectedTier) {
      setError("Please select an access level");
      return;
    }
    
    setPurchasing(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/access/${token}/checkout`, {
        ticket_tier: selectedTier,
        poker_interest: pokerInterest,
        streaming_interest: streamingInterest,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
      setPurchasing(false);
    }
  };

  const ticketTiers = [
    {
      tier: "room_access",
      name: "Room Access",
      price: 1000,
      description: "Full two-day event access, Friday night experience, general event areas, live broadcasts and Saturday experiences."
    },
    {
      tier: "sovereign_access",
      name: "Sovereign Access",
      price: 2500,
      description: "Everything above plus elevated hospitality, priority access, private meeting priority, premium experiences, priority consideration for streaming and additional VIP benefits."
    },
    {
      tier: "founding_circle",
      name: "Founding Circle",
      price: 5000,
      description: "The highest and most limited level. Everything above plus the greatest level of access, premium seating where applicable, highest priority for private meeting rooms and streaming opportunities, exclusive Founding Circle experiences, premium gifting and concierge access."
    }
  ];

  if (loading) {
    return (
      <main className="min-h-screen bg-void flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
      </main>
    );
  }

  if (!accessInfo?.valid) {
    return (
      <main className="min-h-screen bg-void flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-stone-500 mx-auto mb-6" />
          <h1 className="font-cinzel text-2xl text-parchment mb-4">Invalid Access Link</h1>
          <p className="font-manrope text-stone-400 mb-8">
            This access link is invalid or has expired. If you believe this is an error, please contact us.
          </p>
          <a 
            href="mailto:info@sovereigngroup.io"
            className="font-manrope text-sm text-gold-500 hover:text-gold-400 transition-colors"
          >
            info@sovereigngroup.io
          </a>
        </div>
      </main>
    );
  }

  if (success) {
    const tierInfo = ticketTiers.find(t => t.tier === (purchaseDetails?.ticket_tier || selectedTier));
    
    return (
      <main className="min-h-screen bg-void">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-20 h-20 rounded-full border-2 border-gold-500 flex items-center justify-center mx-auto mb-8">
              <Check className="w-10 h-10 text-gold-500" />
            </div>
            
            <p className="font-manrope text-[11px] tracking-[0.3em] text-gold-500 uppercase mb-4">
              Welcome to The Room
            </p>
            
            <h1 className="font-cinzel text-4xl md:text-5xl text-parchment mb-6">
              Your Place is Secured
            </h1>
            
            <p className="font-manrope text-stone-400 mb-12 max-w-lg mx-auto">
              {accessInfo.name}, your {tierInfo?.name || "access"} has been reserved. 
              We'll be in touch with next steps as we approach December.
            </p>
            
            <div className="border border-stone-700 p-8 mb-12 text-left">
              <p className="font-manrope text-[10px] tracking-[0.2em] text-stone-500 uppercase mb-4">Reservation Details</p>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-manrope text-stone-400">Name</span>
                  <span className="font-manrope text-parchment">{accessInfo.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-manrope text-stone-400">Access Level</span>
                  <span className="font-manrope text-gold-500">{tierInfo?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-manrope text-stone-400">Event</span>
                  <span className="font-manrope text-parchment">December 11-12, 2026</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-manrope text-stone-400">Location</span>
                  <span className="font-manrope text-parchment">Las Vegas</span>
                </div>
              </div>
            </div>
            
            <p className="font-manrope text-sm text-stone-500">
              Questions? Contact <a href="mailto:info@sovereigngroup.io" className="text-gold-500 hover:text-gold-400">info@sovereigngroup.io</a>
            </p>
          </motion.div>
        </div>
      </main>
    );
  }

  const foundingCircleRemaining = ticketStats?.founding_circle?.remaining || 0;
  const foundingCircleSoldOut = foundingCircleRemaining <= 0;

  return (
    <main className="min-h-screen bg-void">
      {/* Hero Section */}
      <section className="py-20 md:py-28 border-b border-stone-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <p className="font-manrope text-[11px] tracking-[0.3em] text-gold-500 uppercase mb-6">
              Your Access Has Been Approved
            </p>
            
            <h1 className="font-cinzel text-5xl md:text-7xl text-parchment mb-8">
              THE ROOM
            </h1>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-stone-400 mb-8">
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gold-500" />
                <span className="font-manrope text-sm">Las Vegas</span>
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gold-500" />
                <span className="font-manrope text-sm">December 11-12, 2026</span>
              </span>
            </div>
            
            <p className="font-cormorant text-xl md:text-2xl text-stone-300 italic mb-4">
              300 curated attendees. Two days. One unforgettable night.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Welcome Message */}
      <section className="py-16 border-b border-stone-800">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <p className="font-manrope text-lg text-parchment mb-4">
              {accessInfo.name},
            </p>
            <p className="font-cormorant text-2xl text-stone-300 leading-relaxed">
              You were selected because we believe you belong in The Room.
            </p>
            <p className="font-manrope text-sm text-stone-500 mt-6">
              Your invitation provides access to purchase one of the limited experiences below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ticket Selection */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="font-manrope text-[10px] tracking-[0.2em] text-stone-500 uppercase mb-4">
              Choose Your Level
            </p>
            <h2 className="font-cinzel text-3xl md:text-4xl text-parchment">
              How far inside do you want to go?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {ticketTiers.map((tier, idx) => (
              <motion.div
                key={tier.tier}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
              >
                <TicketTier
                  {...tier}
                  selected={selectedTier === tier.tier}
                  onSelect={setSelectedTier}
                  remaining={tier.tier === "founding_circle" ? foundingCircleRemaining : null}
                  soldOut={tier.tier === "founding_circle" && foundingCircleSoldOut}
                />
              </motion.div>
            ))}
          </div>

          {/* Additional Interests */}
          <motion.div 
            className="max-w-2xl mx-auto border border-stone-700 p-8 mb-12"
            {...fadeUp}
          >
            <p className="font-manrope text-[10px] tracking-[0.2em] text-stone-500 uppercase mb-6">
              Additional Interests
            </p>
            
            <div className="space-y-4">
              <label className="flex items-start gap-4 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={pokerInterest}
                  onChange={(e) => setPokerInterest(e.target.checked)}
                  className="w-5 h-5 mt-0.5 accent-gold-500"
                />
                <div>
                  <p className="font-manrope text-parchment group-hover:text-gold-500 transition-colors">
                    THE ROOM: ALL IN
                  </p>
                  <p className="font-manrope text-sm text-stone-500">
                    I'm interested in the Saturday poker event
                  </p>
                </div>
              </label>
              
              <label className="flex items-start gap-4 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={streamingInterest}
                  onChange={(e) => setStreamingInterest(e.target.checked)}
                  className="w-5 h-5 mt-0.5 accent-gold-500"
                />
                <div>
                  <p className="font-manrope text-parchment group-hover:text-gold-500 transition-colors">
                    Streaming Opportunities
                  </p>
                  <p className="font-manrope text-sm text-stone-500">
                    I'm interested in broadcasting live from The Room
                  </p>
                </div>
              </label>
            </div>
          </motion.div>

          {/* Error Message */}
          {error && (
            <div className="max-w-2xl mx-auto mb-8 p-4 border border-red-500/30 bg-red-500/10 text-center">
              <p className="font-manrope text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Purchase Button */}
          <motion.div className="text-center" {...fadeUp}>
            <button
              onClick={handleCheckout}
              disabled={!selectedTier || purchasing}
              className="inline-flex items-center gap-3 px-12 py-5 bg-gold-500 text-charcoal font-manrope text-sm tracking-[0.15em] uppercase hover:bg-gold-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="secure-access-btn"
            >
              {purchasing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Connecting to payment...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Purchase Access
                  {selectedTier && (
                    <span className="text-charcoal/70">
                      · ${ticketTiers.find(t => t.tier === selectedTier)?.price.toLocaleString()}
                    </span>
                  )}
                </>
              )}
            </button>
            
            <p className="font-manrope text-xs text-stone-600 mt-4">
              Secure payment via Stripe
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-stone-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-manrope text-xs text-stone-600">
            Questions? Contact <a href="mailto:info@sovereigngroup.io" className="text-stone-500 hover:text-gold-500 transition-colors">info@sovereigngroup.io</a>
          </p>
        </div>
      </footer>
    </main>
  );
};

export default AccessPage;
